// AdminShell — top nav + content area, shared by every authorized page.
// Branches on useAdmin() so each child page is guaranteed `authorized`
// by the time it renders: the gate lives here, not in every page.
//
// Pages mount as:
//   <AdminShell><DraftsPage /></AdminShell>
//
// In the loading / signed_out / not_authorized / error states, the shell
// renders a friendly full-screen message instead of the children.

import { Link, NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAdmin } from '@/auth/use-admin';

interface AdminShellProps {
  children: ReactNode;
}

export function AdminShell({ children }: AdminShellProps) {
  const admin = useAdmin();

  if (admin.state === 'loading') {
    return <FullPage><p className="text-slate-500">Loading…</p></FullPage>;
  }

  if (admin.state === 'signed_out') {
    return (
      <FullPage>
        <h1 className="text-2xl font-semibold tracking-tight">tee admin</h1>
        <p className="mt-2 text-slate-600">
          Sign in with your admin account to continue.
        </p>
        <div className="mt-6">
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              void admin.signIn();
            }}
          >
            Sign in
          </button>
        </div>
      </FullPage>
    );
  }

  if (admin.state === 'not_authorized') {
    return (
      <FullPage>
        <h1 className="text-2xl font-semibold tracking-tight">Not authorized</h1>
        <p className="mt-2 text-slate-600">
          Your account is signed in but is not on the admin allow-list. If
          you believe this is a mistake, contact the founder.
        </p>
        <div className="mt-6">
          <button type="button" className="btn-outline" onClick={() => admin.signOut()}>
            Sign out
          </button>
        </div>
      </FullPage>
    );
  }

  if (admin.state === 'error') {
    return (
      <FullPage>
        <h1 className="text-2xl font-semibold tracking-tight">
          Something went wrong
        </h1>
        <p className="mt-2 text-slate-600">
          Couldn't reach the admin API (<code className="font-mono text-sm">
            {admin.error.message}
          </code>
          ). Try again in a moment.
        </p>
        <div className="mt-6">
          <button
            type="button"
            className="btn-outline"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </FullPage>
    );
  }

  // Authorized: render the chrome + the page content.
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav email={admin.me.email} onSignOut={() => admin.signOut()} />
      <main className="flex-1 mx-auto w-full max-w-6xl px-6 py-10">
        {children}
      </main>
    </div>
  );
}

function TopNav({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const location = useLocation();
  return (
    <nav className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-6xl px-6 py-3 flex items-center gap-6">
        <Link
          to="/"
          className="text-base font-semibold tracking-tight text-slate-900"
        >
          tee admin
        </Link>
        <div className="flex-1 flex items-center gap-4 text-sm">
          <NavItem to="/drafts" current={location.pathname}>Drafts</NavItem>
          <NavItem to="/catalog" current={location.pathname}>Catalog</NavItem>
          <NavItem to="/menu-items/cleanup" current={location.pathname}>Cleanup</NavItem>
          <NavItem to="/menu-items/edit" current={location.pathname}>Edit items</NavItem>
          <NavItem to="/identity/label" current={location.pathname}>Labels</NavItem>
          <NavItem to="/usage" current={location.pathname}>Usage</NavItem>
          <NavItem to="/settings" current={location.pathname}>Settings</NavItem>
        </div>
        <span className="text-xs text-slate-500" title={email}>{email}</span>
        <button
          type="button"
          className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
          onClick={onSignOut}
        >
          Sign out
        </button>
      </div>
    </nav>
  );
}

function NavItem({
  to,
  children,
  current,
}: {
  to: string;
  children: ReactNode;
  current: string;
}) {
  // NavLink's built-in `isActive` only matches the route's pathname exactly;
  // we want "/drafts/123" to keep "Drafts" highlighted. Use the prefix
  // comparison + the root case.
  const active = current === to || current.startsWith(`${to}/`);
  return (
    <NavLink
      to={to}
      className={
        active
          ? 'text-slate-900 font-medium border-b-2 border-accent pb-3 -mb-3'
          : 'text-slate-600 hover:text-slate-900'
      }
    >
      {children}
    </NavLink>
  );
}

function FullPage({ children }: { children: ReactNode }) {
  return <main className="mx-auto max-w-3xl px-6 py-16">{children}</main>;
}
